import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cn from 'classnames';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Portal from 'portal/Portal';
import styles from '../index.module.scss';
import Image from 'components/Image';
import stripeLogo from 'images/powered_by_stripe.svg';
import FormHeader from 'components/Header/formHeader';
import { getSecurityId } from 'reducers/users';
import {
  fetchSecurityCompanyDetail,
  fetchBillingInfo,
} from 'reducers/securityCompany';
import { createError } from 'reducers/ui';
import { errorHandler } from 'utils/errorMessages';
import API from 'services/api';
import { useTranslation } from 'react-i18next';
import { stripeForm } from 'utils/constants/css';

const useStyles = makeStyles(theme => ({
  colorPrimary: {
    color: theme.palette.text.primary,
  },
  formContainer: {
    background: theme.palette.background.paper,
    overflowY: 'hidden',
    position: 'relative',
    width: stripeForm.WIDTH,
    height: stripeForm.HEIGHT,
  },
  cardElementColor: {
    background: theme.palette.secondary.light,
  },
}));

const AddPayment = ({
  close,
  securityCompanyId,
  fetchBillingInfo,
  fetchSecurityCompanyDetail,
  createError,
  message,
  header = 'Add Payment',
  last4Num,
}) => {
  const { t } = useTranslation();
  const [succeeded, setSucceeded] = useState(false);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState('');
  const [disabled, setDisabled] = useState(true);
  const [clientSecret, setClientSecret] = useState('');
  const stripe = useStripe();
  const elements = useElements();
  const classes = useStyles();
  const successMessage = t('Billing info has been added successfully');

  useEffect(() => {
    async function loadBillingSetupInfo() {
      try {
        const {
          data: billingSetupInfo,
        } = await API.fetchSecurityCompanyBillingSetupInfo();
        setClientSecret(billingSetupInfo.data.clientSecret);
      } catch (err) {
        createError(errorHandler(err));
      }
    }
    loadBillingSetupInfo();
  }, [createError]);

  const cardStyle = {
    style: {
      base: {
        color: '#32325d',
        fontFamily: 'Arial, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': {
          color: '#32325d',
        },
      },
      invalid: {
        color: '#f44336',
        iconColor: '#f44336',
      },
    },
    hidePostalCode: true,
  };
  const handleChange = async event => {
    setDisabled(!event.complete);
    setError(event.error ? t(event.error.message) : '');
  };

  const handleSubmit = async ev => {
    ev.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    setProcessing(true);
    const payload = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: elements.getElement(CardElement),
      },
    });
    if (payload.error) {
      setError(`${t('Payment failed')} ${t(payload.error.message)}`);
      setProcessing(false);
    } else {
      setError(null);
      setProcessing(false);
      setSucceeded(true);
      fetchSecurityCompanyDetail(securityCompanyId);
      setTimeout(() => {
        fetchBillingInfo();
        close();
      }, 3000);
    }
  };

  return (
    <Portal id={'portal'} type={'modal'} creditCard={true}>
      <div className={classes.formContainer}>
        <FormHeader
          title={header}
          icon={Boolean(last4Num)}
          onClickClose={!processing && close}
          saveHoverText={t('Save Payment Info')}
        />
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.noCardMessage}>{message}</div>
          <CardElement
            id="card-element"
            options={cardStyle}
            onChange={handleChange}
            className={cn(styles.cardElement, classes.cardElementColor)}
          />
          <button
            className={styles.addButton}
            disabled={processing || disabled || succeeded}
          >
            <span>
              {processing ? (
                <CircularProgress size={20} className={classes.colorPrimary} />
              ) : (
                t('Add')
              )}
            </span>
          </button>
          {error && <div className={styles.error}>{error}</div>}
          <p
            className={
              succeeded
                ? styles.resultMessage
                : cn(styles.resultMessage, styles.hidden)
            }
          >
            {succeeded && successMessage}
          </p>
        </form>
        <div className={styles.stripeLogoContainer}>
          <Image src={stripeLogo} className={styles.stripeLogo} />
        </div>
      </div>
    </Portal>
  );
};

AddPayment.propTypes = {
  close: PropTypes.func,
  securityCompanyId: PropTypes.string,
  fetchSecurityCompanyDetail: PropTypes.func,
  createError: PropTypes.func,
  fetchBillingInfo: PropTypes.func,
  message: PropTypes.string,
  header: PropTypes.string,
  last4Num: PropTypes.bool,
};
const mapStateToProps = state => ({
  securityCompanyId: getSecurityId(state),
});
const mapDispatchToProps = dispatch => ({
  fetchBillingInfo: () => dispatch(fetchBillingInfo()),
  fetchSecurityCompanyDetail: securityCompanyId =>
    dispatch(fetchSecurityCompanyDetail(securityCompanyId)),
  createError: err => dispatch(createError(err)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddPayment);
